'use client';

/**
 * One share panel for the whole app (Gordon 2026-11-02: "sharing a track,
 * release, or anything on the app should be easy to share to another user on
 * the app"). Every share — the Share buttons and anything that calls
 * lib/share's shareContent() — opens this panel instead of jumping straight to
 * the phone's share sheet:
 *
 *   • Send to a BB member — your recent chats, or search anyone on BB; pick one
 *     or more, add a note, and each gets the thing as a card in their chat
 *   • Copy link · Share via… (the phone/computer's own share sheet) · QR code
 *
 * Mounted once in app-shell (ShareSheetHost); lib/share finds it through
 * window.__bbShareSheet and falls back to the old behaviour without it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { collection, endAt, getDocs, limit, orderBy, query, startAt, where } from 'firebase/firestore';
import { Check, Copy, Link2, Loader2, QrCode, Search, Send, Share2, Users } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getOrCreateDMRoom, sendMessage, shareContent as shareToRoom, type SharedContent } from '@/services/chat';
import { absoluteShareUrl, inAppPath, shareCardImage, type ShareRequest } from '@/lib/share';

interface Target { key: string; kind: 'room' | 'user'; id: string; name: string; image?: string; group?: boolean }

declare global {
  interface Window { __bbShareSheet?: (req: ShareRequest) => Promise<boolean> }
}

const isPlaceholder = (d: any) => d?.accountType === 'ghost' || d?.placeholder === true;

export function ShareSheetHost() {
  const t = useTranslations('shareSheet');
  const { user, userProfile } = useAuth() as any;
  const [req, setReq] = useState<ShareRequest | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const result = useRef(false);

  // Register the panel for lib/share.
  useEffect(() => {
    window.__bbShareSheet = (r: ShareRequest) => new Promise<boolean>(resolve => {
      resolver.current?.(false);
      resolver.current = resolve;
      result.current = false;
      setReq(r);
    });
    return () => { delete window.__bbShareSheet; };
  }, []);

  const close = useCallback(() => {
    setReq(null);
    resolver.current?.(result.current);
    resolver.current = null;
  }, []);

  return (
    <Dialog open={!!req} onOpenChange={o => { if (!o) close(); }}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5" aria-hidden="true" />{t('title')}</DialogTitle>
        </DialogHeader>
        {req && (
          <ShareBody key={req.url + req.title} req={req} user={user} profile={userProfile}
            onShared={() => { result.current = true; }} onDone={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareBody({ req, user, profile, onShared, onDone }: {
  req: ShareRequest; user: any; profile: any; onShared: () => void; onDone: () => void;
}) {
  const t = useTranslations('shareSheet');
  const url = useMemo(() => absoluteShareUrl(req), [req]);
  const path = useMemo(() => inAppPath(req), [req]);
  const image = req.imageUrl || shareCardImage(path);
  const [recent, setRecent] = useState<Target[] | null>(null);
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<Target[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<Target[]>([]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'' | 'sent' | 'failed'>('');
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const me = user?.uid as string | undefined;
  const myName = profile?.displayName || user?.displayName || 'BB member';
  const myImage = profile?.profileImageUrl || user?.photoURL || '';

  // Your chats, newest first.
  useEffect(() => {
    if (!me) return;
    let alive = true;
    getDocs(query(collection(db, 'chatRooms'), where('participants', 'array-contains', me), limit(60)))
      .then(snap => {
        if (!alive) return;
        const rooms = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))
          .sort((a, b) => (b.lastMessageAt?.seconds || 0) - (a.lastMessageAt?.seconds || 0))
          .slice(0, 10)
          .map((r): Target => {
            if (r.type === 'direct') {
              const other = (r.participants || []).find((p: string) => p !== me) || '';
              return { key: `room:${r.id}`, kind: 'room', id: r.id, name: r.participantNames?.[other] || 'BB member', image: r.participantAvatars?.[other] || '' };
            }
            return { key: `room:${r.id}`, kind: 'room', id: r.id, name: r.groupName || r.artistName || 'Group', image: r.groupAvatar || r.artistImageUrl || '', group: true };
          });
        setRecent(rooms);
      })
      .catch(() => alive && setRecent([]));
    return () => { alive = false; };
  }, [me]);

  // Anyone on BB, by name (as typed and capitalised — Firestore prefix search is case-sensitive).
  useEffect(() => {
    const q = term.trim();
    if (!q || !me) { setFound([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const variants = [...new Set([q, q.charAt(0).toUpperCase() + q.slice(1)])];
        const snaps = await Promise.all(variants.map(v => getDocs(query(
          collection(db, 'users'), orderBy('displayName'), startAt(v), endAt(v + ''), limit(8),
        )).catch(() => null)));
        const seen = new Set<string>();
        const out: Target[] = [];
        snaps.forEach(s => s?.forEach(d => {
          const data = d.data();
          if (d.id === me || seen.has(d.id) || isPlaceholder(data)) return;
          seen.add(d.id);
          out.push({ key: `user:${d.id}`, kind: 'user', id: d.id, name: data.displayName || 'BB member', image: data.profileImageUrl || data.photoURL || '' });
        }));
        setFound(out);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [term, me]);

  const toggle = (x: Target) => setPicked(p => (p.some(y => y.key === x.key) ? p.filter(y => y.key !== x.key) : [...p, x]));

  const send = async () => {
    if (!me || !picked.length) return;
    setSending(true);
    setStatus('');
    const card: SharedContent = {
      contentType: req.contentType || (path ? 'page' : 'link'),
      title: req.title,
      link: path || url,
      ...(req.contentId ? { contentId: req.contentId } : {}),
      ...(req.subtitle || req.text ? { subtitle: req.subtitle || req.text } : {}),
      ...(image ? { imageUrl: image } : {}),
      ...(req.audioUrl ? { audioUrl: req.audioUrl } : {}),
    };
    let ok = 0;
    for (const x of picked) {
      try {
        const roomId = x.kind === 'room' ? x.id
          : (await getOrCreateDMRoom(me, myName, myImage, x.id, x.name, x.image || '')).id;
        if (note.trim()) await sendMessage(roomId, me, myName, note.trim(), myImage);
        await shareToRoom(roomId, me, myName, myImage, card);
        ok++;
      } catch (e) {
        console.error('[share] send failed', x.key, e);
      }
    }
    setSending(false);
    if (ok) {
      setStatus('sent');
      setTimeout(onDone, 1100);
    } else setStatus('failed');
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch {
      const ta = document.createElement('textarea');
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    onShared();
  };

  const native = async () => {
    try {
      await navigator.share({ title: req.title, text: req.text || req.title, url });
      onShared();
      onDone();
    } catch { /* cancelled */ }
  };

  const list = term.trim() ? found : (recent || []);
  const row = (x: Target) => {
    const on = picked.some(y => y.key === x.key);
    return (
      <button key={x.key} type="button" onClick={() => toggle(x)} aria-pressed={on}
        className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors', on ? 'bg-foreground text-background' : 'hover:bg-muted')}>
        <Avatar className="h-8 w-8">
          <AvatarImage src={x.image || undefined} alt="" />
          <AvatarFallback className="text-[10px]" name={x.name}>{x.name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{x.name}</span>
        {x.group && <Users className="h-3.5 w-3.5 shrink-0 opacity-60" aria-label={t('group')} />}
        {on && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* What's being shared */}
      <div className="flex items-center gap-3 rounded-xl border border-border p-2">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          {image ? <img src={image} alt="" className="h-full w-full object-cover" /> : <Link2 className="h-5 w-5 opacity-60" aria-hidden="true" />}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{req.title}</span>
          <span className="block truncate text-xs text-muted-foreground">{url.replace(/^https?:\/\//, '')}</span>
        </span>
      </div>

      {/* Send to a BB member */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t('sendTo')}</h3>
        {!me ? (
          <a href={`/signin?redirect=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')}`}
            className="block rounded-lg border border-dashed border-border p-3 text-sm underline-offset-2 hover:underline">{t('signIn')}</a>
        ) : (
          <>
            <label className="relative block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" aria-hidden="true" />
              <input type="search" value={term} onChange={e => setTerm(e.target.value)} placeholder={t('searchPlaceholder')} aria-label={t('searchPlaceholder')}
                className="h-10 w-full rounded-lg border border-foreground/40 bg-background pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
            </label>
            {!term.trim() && <p className="text-xs text-muted-foreground">{t('recent')}</p>}
            <div className="max-h-52 space-y-0.5 overflow-y-auto">
              {(recent === null && !term.trim()) || searching
                ? <p className="py-3 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" /></p>
                : list.length === 0
                  ? <p className="py-3 text-center text-sm text-muted-foreground">{term.trim() ? t('noResults') : t('noChats')}</p>
                  : list.map(row)}
              {/* Picked people who aren't in the current list stay visible. */}
              {picked.filter(p => !list.some(x => x.key === p.key)).map(row)}
            </div>
            {picked.length > 0 && (
              <div className="space-y-2">
                <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('note')} aria-label={t('note')} maxLength={500}
                  className="h-10 w-full rounded-lg border border-foreground/40 bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
                <Button onClick={() => void send()} disabled={sending || status === 'sent'} className="w-full gap-2">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : status === 'sent' ? <Check className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  {status === 'sent' ? t('sent') : t('sendToN', { n: picked.length })}
                </Button>
                {status === 'failed' && <p className="text-sm font-medium text-red-600" role="alert">{t('failed')}</p>}
              </div>
            )}
          </>
        )}
      </section>

      {/* Everywhere else */}
      <section className="grid grid-cols-3 gap-2 border-t border-border pt-3">
        <button type="button" onClick={() => void copy()} className="flex flex-col items-center gap-1 rounded-lg border border-border p-2.5 text-xs font-medium hover:border-foreground">
          {copied ? <Check className="h-5 w-5" aria-hidden="true" /> : <Copy className="h-5 w-5" aria-hidden="true" />}
          {copied ? t('copied') : t('copy')}
        </button>
        <button type="button" onClick={() => void native()} disabled={typeof navigator === 'undefined' || !('share' in navigator)}
          className="flex flex-col items-center gap-1 rounded-lg border border-border p-2.5 text-xs font-medium hover:border-foreground disabled:opacity-40">
          <Share2 className="h-5 w-5" aria-hidden="true" />{t('shareVia')}
        </button>
        <button type="button" onClick={() => setQr(q => !q)} aria-pressed={qr}
          className="flex flex-col items-center gap-1 rounded-lg border border-border p-2.5 text-xs font-medium hover:border-foreground">
          <QrCode className="h-5 w-5" aria-hidden="true" />{t('qr')}
        </button>
      </section>
      {qr && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&data=${encodeURIComponent(url)}`}
          alt={t('qr')} width={240} height={240} className="mx-auto rounded-lg bg-white p-2" />
      )}
    </div>
  );
}
